import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Search,
  Lightbulb,
  HeartHandshake,
  MessageSquare,
  BarChart3,
  GraduationCap,
  Clock,
  DollarSign,
  Smile,
  Target,
  Rocket,
  TrendingUp,
  ArrowRight,
  Check,
  ChevronRight,
  Store,
  Stethoscope,
  Building2,
  Truck,
  Hotel,
  Factory,
  Users,
  Calendar,
  Phone,
  Heart,
  X,
  Send,
  Loader,
  Briefcase
} from 'lucide-react';

// API Base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ============= CONSULTATION MODAL =============
const ConsultationModal = ({ isOpen, onClose, selectedPlan = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    industry: '',
    employees: '',
    challenge: '',
    package: selectedPlan || '',
    message: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });

  // Update package when selectedPlan changes
  React.useEffect(() => {
    if (selectedPlan) {
      setFormData(prev => ({ ...prev, package: selectedPlan }));
    }
  }, [selectedPlan]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.challenge) {
      setStatus({ type: 'error', message: 'Please fill in all required fields' });
      return;
    }

    setStatus({ type: 'loading', message: '' });

    try {
      const response = await fetch(`${API_BASE_URL}/api/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || 'Not provided',
          interest: 'AI Consulting Services',
          message: `Consulting Request\n\nCompany: ${formData.company || 'Not specified'}\nIndustry: ${formData.industry || 'Not specified'}\nCompany Size: ${formData.employees || 'Not specified'}\nPackage Interest: ${formData.package || 'Not specified'}\n\nMain Challenge:\n${formData.challenge}\n\nAdditional Details:\n${formData.message || 'None provided'}`
        }),
      });

      const data = await response.json();

      if (data.success) {
        setStatus({ type: 'success', message: 'Request submitted successfully!' });
        setFormData({ name: '', email: '', phone: '', company: '', industry: '', employees: '', challenge: '', package: '', message: '' });
      } else {
        throw new Error(data.error || 'Failed to submit');
      }
    } catch (error) {
      console.error('Consultation error:', error);
      setStatus({ type: 'error', message: 'Failed to submit. Please try again.' });
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (status.type === 'error') setStatus({ type: '', message: '' });
  };

  const resetModal = () => {
    setStatus({ type: '', message: '' });
    setFormData({ name: '', email: '', phone: '', company: '', industry: '', employees: '', challenge: '', package: selectedPlan || '', message: '' });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative bg-slate-950 backdrop-blur-xl border border-white/[0.08] rounded-3xl p-8 max-w-md w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-emerald-500/5 rounded-3xl" />
        
        <button
          onClick={resetModal}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition z-10"
        >
          <X size={24} />
        </button>

        {status.type === 'success' ? (
          <motion.div 
            className="text-center py-8 relative z-10"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="mb-4 inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-blue-500/20 to-emerald-500/20 border border-teal-500/30">
              <Check size={32} className="text-teal-300" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">Request Received!</h3>
            <p className="text-gray-400 mb-6">
              Thank you for reaching out! We'll review your request and schedule a free consultation call within 24 hours.
            </p>
            <button
              onClick={resetModal}
              className="px-6 py-2 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-white font-semibold hover:shadow-lg hover:shadow-teal-500/30 transition"
            >
              Close
            </button>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className="relative z-10">
            <div className="mb-2 inline-flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-blue-500/20 to-emerald-500/20 border border-teal-500/30 rounded-full">
              <Phone size={14} className="text-teal-400" />
              <span className="text-xs text-teal-400 font-semibold">Free Consultation</span>
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Schedule Your Call</h2>
            <p className="text-teal-400 mb-6">No obligation, just honest advice</p>

            {status.type === 'error' && (
              <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                {status.message}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Full Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Email Address *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition"
                  placeholder="Enter your email"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Phone Number</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition"
                  placeholder="Best number to reach you"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Company</label>
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) => handleChange('company', e.target.value)}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition"
                    placeholder="Company name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Team Size</label>
                  <select
                    value={formData.employees}
                    onChange={(e) => handleChange('employees', e.target.value)}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-teal-500 focus:outline-none transition"
                  >
                    <option value="" className="bg-slate-950">Select</option>
                    <option value="1-10" className="bg-slate-950">1-10</option>
                    <option value="11-50" className="bg-slate-950">11-50</option>
                    <option value="51-200" className="bg-slate-950">51-200</option>
                    <option value="200+" className="bg-slate-950">200+</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Industry</label>
                <select
                  value={formData.industry}
                  onChange={(e) => handleChange('industry', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-teal-500 focus:outline-none transition"
                >
                  <option value="" className="bg-slate-950">Select your industry</option>
                  <option value="Retail / E-commerce" className="bg-slate-950">Retail / E-commerce</option>
                  <option value="Healthcare" className="bg-slate-950">Healthcare</option>
                  <option value="Finance / Banking" className="bg-slate-950">Finance / Banking</option>
                  <option value="Manufacturing" className="bg-slate-950">Manufacturing</option>
                  <option value="Professional Services" className="bg-slate-950">Professional Services</option>
                  <option value="Hospitality" className="bg-slate-950">Hospitality</option>
                  <option value="Logistics / Transportation" className="bg-slate-950">Logistics / Transportation</option>
                  <option value="Real Estate" className="bg-slate-950">Real Estate</option>
                  <option value="Education" className="bg-slate-950">Education</option>
                  <option value="Other" className="bg-slate-950">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Package Interest</label>
                <select
                  value={formData.package}
                  onChange={(e) => handleChange('package', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-teal-500 focus:outline-none transition"
                >
                  <option value="" className="bg-slate-950">Select package</option>
                  <option value="Discovery Call (Free)" className="bg-slate-950">Discovery Call (Free)</option>
                  <option value="AI Strategy Package ($5K)" className="bg-slate-950">AI Strategy Package ($5K)</option>
                  <option value="Ongoing Advisory ($3K/mo)" className="bg-slate-950">Ongoing Advisory ($3K/mo)</option>
                  <option value="Not sure yet" className="bg-slate-950">Not sure yet</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">What's your biggest challenge? *</label>
                <textarea
                  value={formData.challenge}
                  onChange={(e) => handleChange('challenge', e.target.value)}
                  rows="3"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition resize-none"
                  placeholder="Tell us about your main pain point or what you'd like AI to help with..."
                />
              </div>

              <button
                type="submit"
                disabled={status.type === 'loading'}
                className="w-full py-3 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-white font-semibold hover:shadow-lg hover:shadow-teal-500/30 transition disabled:opacity-70 flex items-center justify-center gap-2"
              >
                {status.type === 'loading' ? (
                  <>
                    <Loader size={18} className="animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Calendar size={18} />
                    Book Free Consultation
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
};

// ============= NAVBAR =============
const Navbar = ({ onConsultation }) => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.nav
      className={`fixed w-full top-0 z-50 transition-all duration-300 ${
        scrolled ? 'backdrop-blur-xl bg-slate-950/80 border-b border-white/5' : 'backdrop-blur-sm bg-transparent'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-teal-500" />
            <span className="text-white">AI Factory</span>
          </Link>

          <div className="hidden md:flex items-center gap-12">
            <Link to="/" className="text-sm text-gray-300 hover:text-white transition">Home</Link>
            <Link to="/services" className="text-sm text-gray-300 hover:text-white transition">Services</Link>
            <a href="#process" className="text-sm text-gray-300 hover:text-white transition">Process</a>
            <a href="#pricing" className="text-sm text-gray-300 hover:text-white transition">Pricing</a>
          </div>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onConsultation}
            className="hidden md:block px-6 py-2.5 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-sm font-semibold text-white hover:shadow-lg hover:shadow-teal-500/50 transition"
          >
            Free Consultation
          </motion.button>
        </div>
      </div>
    </motion.nav>
  );
};

// ============= GRADIENT ORBS BACKGROUND =============
const GradientOrbs = () => {
  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden">
      <motion.div
        className="absolute -top-40 -left-40 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl"
        animate={{
          x: [0, 30, -30, 0],
          y: [0, 40, -40, 0],
        }}
        transition={{ duration: 8, repeat: Infinity }}
      />
      <motion.div
        className="absolute top-1/2 -right-40 w-96 h-96 bg-emerald-600/20 rounded-full blur-3xl"
        animate={{
          x: [0, -30, 30, 0],
          y: [0, -40, 40, 0],
        }}
        transition={{ duration: 10, repeat: Infinity }}
      />
      <motion.div
        className="absolute -bottom-40 left-1/2 w-96 h-96 bg-teal-600/15 rounded-full blur-3xl"
        animate={{
          x: [0, 40, -40, 0],
          y: [0, 30, -30, 0],
        }}
        transition={{ duration: 12, repeat: Infinity }}
      />
    </div>
  );
};

// ============= HERO SECTION =============
const Hero = ({ onConsultation }) => {
  const stats = [
    { value: '200+', label: 'Businesses Helped' },
    { value: '40%', label: 'Average Cost Savings' },
    { value: '98%', label: 'Would Recommend Us' },
    { value: '3-6', label: 'Months to See Results' },
  ];

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      <GradientOrbs />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(20,184,166,0.2),rgba(255,255,255,0))]" />

      <motion.div
        className="relative z-10 text-center max-w-5xl mx-auto px-4 py-20"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        {/* Badges */}
        <motion.div
          className="flex flex-wrap justify-center gap-3 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {['Simple Guidance', 'No Tech Jargon', 'Real Results'].map((badge, i) => (
            <span
              key={i}
              className="text-xs font-semibold text-slate-900 px-4 py-2 rounded-full bg-gradient-to-r from-teal-400 to-emerald-400"
            >
              {badge}
            </span>
          ))}
        </motion.div>

        {/* Title */}
        <motion.h1
          className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight tracking-tight"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <span className="text-white">AI Consulting</span>
          <br />
          <span className="bg-gradient-to-r from-blue-400 via-teal-400 to-emerald-400 bg-clip-text text-transparent">
            Services
          </span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          className="text-xl md:text-2xl text-gray-300 mb-4 font-light tracking-wide"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          Making Smart Technology Work for Your Business
        </motion.p>

        {/* Description */}
        <motion.p
          className="text-base md:text-lg text-gray-400 mb-10 max-w-3xl mx-auto leading-relaxed"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          Confused about AI? We translate complex technology into simple business solutions. 
          Think of us as your friendly guide to understanding how smart computers can help your 
          business save time, cut costs, and serve customers better. No technical degree required—
          we speak your language.
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onConsultation}
            className="px-8 py-4 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-white font-semibold hover:shadow-2xl hover:shadow-teal-500/50 transition flex items-center gap-2"
          >
            Schedule Free Call <ArrowRight size={18} />
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => document.getElementById('success-stories').scrollIntoView({ behavior: 'smooth' })}
            className="px-8 py-4 border border-white/20 rounded-full text-white font-semibold hover:bg-white/10 transition backdrop-blur-sm hover:border-teal-400/40"
          >
            View Success Stories
          </motion.button>
        </motion.div>

        {/* Stats */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              className="relative group"
              whileHover={{ y: -5 }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-teal-500/20 to-blue-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative bg-white/[0.03] backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:border-teal-500/30 transition-all">
                <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent mb-2">
                  {stat.value}
                </div>
                <div className="text-sm text-gray-400 uppercase tracking-wider">
                  {stat.label}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};

// ============= SERVICE CARD =============
const ServiceCard = ({ service, index }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      className="relative group h-full"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      viewport={{ once: true, margin: "-100px" }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 via-transparent to-emerald-500/20 rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      <div className="relative bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-3xl p-8 hover:border-teal-400/30 transition-all duration-500 h-full flex flex-col overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-t-3xl" />

        <div className="relative z-10 flex flex-col h-full">
          <motion.div
            className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500/30 to-emerald-500/30 flex items-center justify-center mb-6 group-hover:from-blue-500/50 group-hover:to-emerald-500/50 transition-all"
            animate={isHovered ? { rotate: [0, -10, 10, 0] } : {}}
            transition={{ duration: 0.5 }}
          >
            <service.icon className="text-teal-300" size={32} />
          </motion.div>

          <h3 className="text-xl font-bold text-white mb-4 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-blue-400 group-hover:via-teal-400 group-hover:to-emerald-400 group-hover:bg-clip-text transition-all">
            {service.title}
          </h3>

          <p className="text-gray-400 mb-6 leading-relaxed flex-grow">
            {service.description}
          </p>

          <ul className="space-y-3">
            {service.features.map((feature, i) => (
              <motion.li
                key={i}
                className="text-sm text-gray-400 flex items-start gap-3"
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 * i }}
              >
                <ChevronRight size={14} className="text-emerald-400 mt-1 flex-shrink-0" />
                <span>{feature}</span>
              </motion.li>
            ))}
          </ul>
        </div>
      </div>
    </motion.div>
  );
};

// ============= SERVICES SECTION =============
const ServicesSection = () => {
  const services = [
    {
      icon: Search,
      title: 'AI Discovery Session',
      description: "Not sure where to start? We'll sit down with you, understand your business challenges, and identify specific areas where AI could help—like a doctor's check-up for your business.",
      features: [
        'Simple conversation about your daily challenges',
        "Identify tasks that waste your team's time",
        'Find opportunities to serve customers better',
        'Get a plain-English report of possibilities',
        'Learn what AI can and cannot do for you'
      ]
    },
    {
      icon: Lightbulb,
      title: 'AI Strategy Planning',
      description: 'We create a simple roadmap showing how to bring AI into your business step-by-step, without disrupting your operations or requiring a huge upfront investment.',
      features: [
        'Easy-to-follow action plan',
        'Realistic timelines and budgets',
        'Start small, grow as you see results',
        'Risk assessment in plain language',
        'Priority list of what to do first'
      ]
    },
    {
      icon: HeartHandshake,
      title: 'Process Automation Advice',
      description: "Show us your repetitive tasks and we'll explain how AI can handle them automatically—like having a tireless assistant that never takes a coffee break.",
      features: [
        'Identify tasks that can run on autopilot',
        'Save hours of manual work every week',
        'Reduce human errors and mistakes',
        'Free up your team for important work',
        'Calculate actual time and money saved'
      ]
    },
    {
      icon: MessageSquare,
      title: 'Customer Service Enhancement',
      description: 'Learn how AI chatbots and smart systems can answer customer questions 24/7, handle common issues instantly, and let your team focus on complex problems.',
      features: [
        '24/7 customer support without hiring night shifts',
        'Instant answers to common questions',
        'Reduce wait times dramatically',
        'Handle multiple customers simultaneously',
        'Improve customer satisfaction scores'
      ]
    },
    {
      icon: BarChart3,
      title: 'Business Intelligence Consulting',
      description: 'We help you understand your data better—think of it as having a crystal ball that uses your past performance to predict future trends and opportunities.',
      features: [
        'Spot patterns in your sales data',
        'Predict what customers will want next',
        'Identify problems before they happen',
        'Make smarter business decisions with data',
        'Easy-to-read reports and dashboards'
      ]
    },
    {
      icon: GraduationCap,
      title: 'AI Education & Training',
      description: 'We teach your team about AI in simple terms—no complicated jargon. Everyone leaves understanding what AI is, what it can do, and how to use it effectively.',
      features: [
        'Interactive workshops for all skill levels',
        'Real examples from your industry',
        'Hands-on practice with AI tools',
        'Overcome fear and resistance to change',
        'Build confidence in using AI daily'
      ]
    }
  ];

  return (
    <section id="services" className="relative py-24 px-4 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 inline-block mb-4">
            What We Do
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Our Consulting Services
          </h2>
          <p className="text-gray-400 max-w-3xl mx-auto text-lg">
            We help you figure out if AI (smart computer technology) makes sense for your business, 
            what it can do for you, and how to get started without breaking the bank.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <ServiceCard key={index} service={service} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= PROCESS SECTION =============
const ProcessSection = () => {
  const steps = [
    {
      number: '01',
      title: 'Get to Know You',
      description: "We start with a friendly conversation about your business. What do you do? What takes up too much time? What frustrates your customers? Think of it as a casual coffee chat where we learn about your world.",
      deliverable: 'A summary of our conversation and initial ideas'
    },
    {
      number: '02',
      title: 'Analyze Your Situation',
      description: "We take a closer look at how your business operates daily. We identify where AI could help most—whether it's saving time, reducing costs, or improving customer experience. Everything explained in plain English.",
      deliverable: 'Easy-to-understand analysis report with opportunities highlighted'
    },
    {
      number: '03',
      title: 'Create Your Roadmap',
      description: "We build a step-by-step plan customized for your business. It shows what to do first, what it'll cost, and how long it'll take. Like following a recipe—simple instructions from start to finish.",
      deliverable: 'Clear action plan with timelines, costs, and expected benefits'
    },
    {
      number: '04',
      title: 'Guide Implementation',
      description: "If you decide to move forward, we help you every step of the way. We work with your existing team or help you find the right technology partners. You're never left figuring things out alone.",
      deliverable: 'Ongoing support, answers to questions, and progress check-ins'
    },
    {
      number: '05',
      title: 'Measure & Improve',
      description: "After launching, we track the results together. Are you saving time? Cutting costs? Making customers happier? We measure what matters and adjust the strategy to keep improving your results.",
      deliverable: 'Regular performance reports showing real impact on your business'
    }
  ];

  return (
    <section id="process" className="relative py-24 px-4 overflow-hidden bg-slate-950/50">
      <div className="max-w-5xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 inline-block mb-4">
            How We Help
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Our Simple Process
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            We work with you in 5 easy steps, making sure you understand everything along the way. 
            No surprises, no confusing tech talk—just clear guidance from start to finish.
          </p>
        </motion.div>

        <div className="relative">
          <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-teal-500/50 via-blue-500/50 to-emerald-500/50 hidden md:block" />

          <div className="space-y-12">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                className={`flex flex-col md:flex-row items-center gap-8 ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                  <motion.div
                    className="bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:border-teal-500/30 transition-all"
                    whileHover={{ scale: 1.02 }}
                  >
                    <h3 className="text-xl font-bold text-teal-400 mb-3">
                      {step.title}
                    </h3>
                    <p className="text-gray-400 mb-4 leading-relaxed">
                      {step.description}
                    </p>
                    <p className="text-sm text-emerald-400">
                      <span className="font-semibold">📋 You Get:</span> {step.deliverable}
                    </p>
                  </motion.div>
                </div>

                <div className="relative z-10">
                  <motion.div
                    className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 via-teal-500 to-emerald-500 flex items-center justify-center text-slate-950 text-2xl font-bold shadow-lg shadow-teal-500/30"
                    whileHover={{ scale: 1.1, rotate: 360 }}
                    transition={{ duration: 0.5 }}
                  >
                    {step.number}
                  </motion.div>
                </div>

                <div className="flex-1 hidden md:block" />
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// ============= BENEFITS SECTION =============
const BenefitsSection = () => {
  const benefits = [
    {
      icon: Clock,
      title: 'Save Massive Amounts of Time',
      description: "Imagine if boring, repetitive tasks happened automatically. Your team stops wasting hours on data entry, answering the same questions, or sorting through emails. Instead, they focus on work that actually needs human creativity and judgment."
    },
    {
      icon: DollarSign,
      title: 'Cut Operational Costs',
      description: "When routine work runs on autopilot, you need fewer people doing manual tasks. You make fewer mistakes that cost money to fix. Plus, you can serve more customers without hiring proportionally more staff. Your profit margins improve naturally."
    },
    {
      icon: Smile,
      title: 'Make Customers Happier',
      description: 'People love quick answers and fast service. AI can respond to customer questions instantly, any time of day. No more "sorry, we\'re closed" or long wait times. Happy customers become loyal customers who tell their friends about you.'
    },
    {
      icon: Target,
      title: 'Make Smarter Decisions',
      description: "AI can spot patterns in your business data that humans might miss. It's like having a super-smart advisor who analyzes everything and gives you recommendations. You stop guessing and start making decisions based on solid insights."
    },
    {
      icon: Rocket,
      title: 'Stay Ahead of Competition',
      description: "Your competitors are probably looking into AI too. Those who adopt it first get a competitive advantage—they're faster, cheaper, and better at serving customers. Don't get left behind while others move forward."
    },
    {
      icon: TrendingUp,
      title: 'Scale Without the Pain',
      description: "Growing usually means hiring more people, buying more equipment, and managing more complexity. With AI, you can handle more work without proportionally increasing costs or headaches. Growth becomes easier and more profitable."
    }
  ];

  return (
    <section className="relative py-24 px-4 overflow-hidden">
      <div className="max-w-6xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 inline-block mb-4">
            Why Choose AI
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            What AI Can Do For You
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            AI might sound complicated, but the benefits are simple: save time, save money, 
            and make your customers happier. Here's what it really means for your business.
          </p>
        </motion.div>

        <motion.div
          className="bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-3xl p-8 md:p-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="space-y-10">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                className="flex flex-col md:flex-row gap-6 items-start group"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500/30 to-emerald-500/30 flex items-center justify-center flex-shrink-0 group-hover:from-blue-500/50 group-hover:to-emerald-500/50 transition-all">
                  <benefit.icon className="text-teal-300" size={28} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-transparent bg-gradient-to-r from-blue-400 to-teal-400 bg-clip-text mb-3">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ============= PRICING SECTION =============
const PricingSection = ({ onSelectPlan }) => {
  const plans = [
    {
      tier: 'Discovery Call',
      price: 'FREE',
      period: '1 Hour Session',
      featured: false,
      planValue: 'Discovery Call (Free)',
      features: [
        'No-pressure conversation',
        'Discuss your challenges',
        'Get initial AI ideas',
        'Understand if AI fits your needs',
        'Ask any questions you have',
        'No commitment required'
      ]
    },
    {
      tier: 'AI Strategy Package',
      price: '$5K',
      period: '2-3 Week Project',
      featured: true,
      planValue: 'AI Strategy Package ($5K)',
      features: [
        'Complete business analysis',
        'Custom AI opportunity report',
        'Detailed implementation roadmap',
        'Budget and timeline estimates',
        'Risk assessment',
        'Technology recommendations',
        '2 follow-up calls included',
        '100% money-back guarantee'
      ]
    },
    {
      tier: 'Ongoing Advisory',
      price: '$3K/mo',
      period: 'Monthly Retainer',
      featured: false,
      planValue: 'Ongoing Advisory ($3K/mo)',
      features: [
        'Unlimited consultation calls',
        'Strategic guidance & support',
        'Help with vendor selection',
        'Project oversight & monitoring',
        'Monthly performance reviews',
        'Priority email support',
        'Quarterly strategy updates',
        'Cancel anytime'
      ]
    }
  ];

  return (
    <section id="pricing" className="relative py-24 px-4 overflow-hidden bg-slate-950/50">
      <div className="max-w-6xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 inline-block mb-4">
            Pricing
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Simple, Honest Pricing
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            No hidden fees or surprise charges. Pick the package that fits your needs and budget. 
            You can always start small and scale up as you see results.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {plans.map((plan, index) => (
            <motion.div
              key={index}
              className={`relative group ${plan.featured ? 'md:-mt-4 md:mb-4' : ''}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              {plan.featured && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                  <span className="px-4 py-1 bg-gradient-to-r from-teal-500 to-emerald-500 rounded-full text-xs font-bold text-slate-900">
                    MOST POPULAR
                  </span>
                </div>
              )}

              <div className={`relative bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border rounded-3xl p-8 h-full flex flex-col transition-all duration-300 ${
                plan.featured 
                  ? 'border-teal-500/50 shadow-lg shadow-teal-500/20' 
                  : 'border-white/10 hover:border-teal-500/30'
              }`}>
                <div className={`absolute top-0 left-0 right-0 h-1 rounded-t-3xl ${
                  plan.featured 
                    ? 'bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500' 
                    : 'bg-gradient-to-r from-blue-500/50 via-teal-500/50 to-emerald-500/50'
                }`} />

                <h3 className="text-xl font-bold text-teal-400 mb-4 text-center">
                  {plan.tier}
                </h3>

                <div className="text-center mb-6">
                  <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent mb-2">
                    {plan.price}
                  </div>
                  <div className="text-gray-400 text-sm">
                    {plan.period}
                  </div>
                </div>

                <ul className="space-y-4 mb-8 flex-grow">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3 text-gray-400">
                      <Check size={18} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => onSelectPlan(plan.planValue)}
                  className={`w-full py-4 rounded-full font-semibold transition-all ${
                    plan.featured
                      ? 'bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 text-white hover:shadow-lg hover:shadow-teal-500/50'
                      : 'border border-white/20 text-white hover:bg-white/10 hover:border-teal-500/50'
                  }`}
                >
                  {plan.price === 'FREE' ? 'Book Free Call' : 'Get Started'}
                </motion.button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= SUCCESS STORIES SECTION =============
const SuccessStoriesSection = () => {
  const stories = [
    {
      icon: Store,
      company: 'Local Retail Chain',
      industry: 'Retail • 12 Stores',
      content: 'They were spending 20+ hours weekly manually tracking inventory across locations. We helped them implement a smart system that predicts what products to stock where. No more overstocking slow items or running out of popular ones.',
      result: 'Saved 18 hours per week, reduced waste by 30%, increased sales by 15%'
    },
    {
      icon: Stethoscope,
      company: 'Medical Clinic',
      industry: 'Healthcare • 50 Staff',
      content: 'Patients were frustrated with long wait times for appointment scheduling and basic questions. We guided them to deploy a smart assistant that handles routine questions and scheduling automatically, freeing up nurses for patient care.',
      result: '70% of questions answered instantly, 40% fewer missed appointments'
    },
    {
      icon: Building2,
      company: 'Accounting Firm',
      industry: 'Professional Services • 25 Employees',
      content: 'Accountants spent hours sorting and categorizing receipts and documents manually. We showed them how AI can read documents automatically, extract key information, and organize everything—like having a super-fast assistant who never makes mistakes.',
      result: 'Saved 200+ hours per month, served 35% more clients with same staff'
    },
    {
      icon: Truck,
      company: 'Distribution Company',
      industry: 'Logistics • 100+ Employees',
      content: 'Their delivery routes were planned manually, wasting fuel and time. We helped them implement smart route optimization that considers traffic, weather, and priority deliveries. Like having a genius dispatcher who never sleeps.',
      result: 'Cut fuel costs by 25%, completed 20% more deliveries per day'
    },
    {
      icon: Hotel,
      company: 'Hotel Chain',
      industry: 'Hospitality • 8 Properties',
      content: 'Front desk staff were overwhelmed with repetitive guest questions about check-in times, amenities, and local attractions. We set up an AI concierge that answers these questions instantly via text or chat, available 24/7 in multiple languages.',
      result: 'Guest satisfaction up 28%, staff can focus on personal service'
    },
    {
      icon: Factory,
      company: 'Manufacturing Plant',
      industry: 'Manufacturing • 200 Workers',
      content: "Equipment breakdowns were costing them thousands in downtime. We helped them implement predictive monitoring that watches machines and warns before problems occur—like a doctor doing preventive health checks instead of emergency surgery.",
      result: 'Reduced unexpected downtime by 60%, saved $500K annually'
    }
  ];

  return (
    <section id="success-stories" className="relative py-24 px-4 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 inline-block mb-4">
            Success Stories
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Real Results from Real Businesses
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            See how other businesses like yours used AI to solve problems and grow. These aren't 
            made-up stories—these are actual results from our consulting work.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {stories.map((story, index) => (
            <motion.div
              key={index}
              className="group relative"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-3xl p-6 hover:border-teal-500/30 transition-all duration-300 h-full flex flex-col">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500/30 to-emerald-500/30 flex items-center justify-center group-hover:from-blue-500/50 group-hover:to-emerald-500/50 transition-all">
                    <story.icon className="text-teal-300" size={28} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-teal-400">
                      {story.company}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {story.industry}
                    </p>
                  </div>
                </div>

                <p className="text-gray-400 leading-relaxed mb-6 flex-grow">
                  {story.content}
                </p>

                <div className="bg-emerald-500/10 border-l-4 border-emerald-500 rounded-r-xl p-4">
                  <div className="flex items-start gap-2">
                    <Heart className="text-emerald-400 flex-shrink-0 mt-0.5" size={16} />
                    <p className="text-sm text-emerald-400 font-medium">
                      Result: {story.result}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= FINAL CTA =============
const FinalCTA = ({ onConsultation }) => {
  return (
    <section className="relative py-24 px-4 overflow-hidden bg-slate-950/50">
      <div className="max-w-4xl mx-auto">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 via-teal-500/20 to-emerald-600/20 blur-3xl" />

        <motion.div
          className="relative bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-3xl p-12 md:p-16 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-t-3xl" />

          <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent">
            Ready to Explore AI?
          </h2>
          <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto">
            Start with a free consultation. No obligation, no pressure—just an honest conversation 
            about whether AI makes sense for your business. Let's talk!
          </p>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onConsultation}
            className="px-10 py-4 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-white font-bold text-lg hover:shadow-2xl hover:shadow-teal-500/50 transition flex items-center gap-3 mx-auto"
          >
            Schedule Free Call <Phone size={20} />
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

// ============= FOOTER =============
const Footer = () => {
  return (
    <footer className="relative border-t border-white/10 py-12 px-4 bg-slate-950">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          <div>
            <Link to="/" className="text-white font-bold mb-4 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-teal-500" />
              AI Factory
            </Link>
            <p className="text-gray-400 text-sm mt-4 leading-relaxed">
              Making AI simple and accessible for businesses of all sizes.
            </p>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 text-xs tracking-wider uppercase">Services</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><Link to="/ai-engineer-augmentation" className="hover:text-teal-400 transition">Staff Augmentation</Link></li>
              <li><Link to="/ai-project-execution" className="hover:text-teal-400 transition">Project Execution</Link></li>
              <li><Link to="/ai-consulting-services" className="hover:text-teal-400 transition">Consulting</Link></li>
              <li><Link to="/custom-ai-ml-solutions" className="hover:text-teal-400 transition">Custom Solutions</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 text-xs tracking-wider uppercase">Company</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><Link to="/#about" className="hover:text-teal-400 transition">About</Link></li>
              <li><Link to="/training" className="hover:text-teal-400 transition">Training Programs</Link></li>
              <li><Link to="/services" className="hover:text-teal-400 transition">All Services</Link></li>
              <li><Link to="/#contact" className="hover:text-teal-400 transition">Contact</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 text-xs tracking-wider uppercase">Legal</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><a href="#" className="hover:text-teal-400 transition">Privacy Policy</a></li>
              <li><a href="#" className="hover:text-teal-400 transition">Terms of Service</a></li>
              <li><a href="#" className="hover:text-teal-400 transition">Cookie Policy</a></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-400 text-sm">© 2025 AI Factory. All rights reserved.</p>
          <div className="flex gap-6">
            <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-teal-400 transition text-sm">Twitter</a>
            <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-teal-400 transition text-sm">LinkedIn</a>
            <a href="https://discord.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-teal-400 transition text-sm">Discord</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// ============= MAIN PAGE =============
export default function AIConsultingServicesPage() {
  const [consultationModal, setConsultationModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const handleConsultation = (plan = null) => {
    setSelectedPlan(plan);
    setConsultationModal(true);
  };

  return (
    <div className="bg-slate-950 min-h-screen overflow-x-hidden text-white">
      <Navbar onConsultation={() => handleConsultation()} />
      <Hero onConsultation={() => handleConsultation()} />
      <ServicesSection />
      <ProcessSection />
      <BenefitsSection />
      <PricingSection onSelectPlan={handleConsultation} />
      <SuccessStoriesSection />
      <FinalCTA onConsultation={() => handleConsultation()} />
      <Footer />

      {/* Consultation Modal */}
      <ConsultationModal
        isOpen={consultationModal}
        onClose={() => setConsultationModal(false)}
        selectedPlan={selectedPlan}
      />
    </div>
  );
}